import React, { useState, Fragment, useEffect } from 'react'
import { Header } from '../components/layout/Header'
import { Footer } from '../components/layout/Footer'
import { MobileFooterNav } from '../components/layout/MobileFooterNav'
import { PharmacistChat } from '../components/services/PharmacistChat'
import { AnnouncementBar } from '../components/layout/AnnouncementBar'
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill'
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb'
import { MobileHomeHeader } from '../components/MobileHomeHeader'
import { NumericInput } from '../components/shared/NumericInput'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  UserIcon,
  ActivityIcon,
  TargetIcon,
  UtensilsIcon,
  DollarSignIcon,
  ClockIcon,
  DropletIcon,
  FlameIcon,
  ScaleIcon,
  HeartIcon,
  AlertCircleIcon,
  CheckCircleIcon,
  SparklesIcon,
  DownloadIcon,
  ChefHatIcon,
  ShoppingCartIcon,
  Loader2Icon,
  BrainIcon,
  MinusIcon,
  PlusIcon,
  TrendingUpIcon,
} from 'lucide-react'
interface NutritionData {
  gender: 'male' | 'female' | ''
  age: string
  height: string
  weight: string
  goal: 'lose' | 'maintain' | 'gain' | ''
  activity: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active' | ''
  dietary: string[]
  cuisine: string
  budget: 'low' | 'medium' | 'high' | ''
  mealsPerDay: '3' | '4' | '5' | '6' | ''
  fastingWindow: 'none' | '16:8' | '14:10' | ''
  allergies: string
  targetRate: 'mild_loss' | 'standard_loss' | 'gain' | ''
}
interface NutritionResults {
  profile: {
    gender: string
    age: number
    height_cm: number
    weight_kg: number
  }
  activity: string
  bmi: number
  bmi_class: string
  bmr: number
  tdee: number
  calorie_target: number
  macros: {
    protein_g: number
    fat_g: number
    carb_g: number
  }
  water_l: number
  fiber_g: number
  ideal_weight_range_kg: [number, number]
  notes: string[]
}
interface FoodItem {
  food: string
  qty_g?: number
  qty_ml?: number
  calories: number
  protein_g: number
  fat_g: number
  carb_g: number
}
interface Meal {
  name: string
  time_range: string
  items: FoodItem[]
}
interface DailyPlan {
  day: number
  total_calories: number
  total_macros: {
    protein_g: number
    fat_g: number
    carb_g: number
  }
  meals: Meal[]
  hydration_l: number
}
interface DietPlan {
  plan_length_days: number
  daily: DailyPlan[]
  weekly_shopping_list: Array<{
    food: string
    qty_g?: number
    qty_ml?: number
  }>
  cost_level: string
  disclaimer: string
}
type Step =
  | 'basic'
  | 'goals'
  | 'activity'
  | 'dietary'
  | 'planning'
  | 'calculating'
  | 'results'
const nutritionTips = [
  'Drink at least 8 glasses of water daily to stay hydrated and support metabolism.',
  'Include protein in every meal to help maintain muscle mass and keep you feeling full.',
  'Choose whole grains over refined grains for sustained energy throughout the day.',
  'Eat a variety of colorful vegetables to ensure you get a wide range of nutrients.',
  'Healthy fats from nuts, seeds, and olive oil support heart health and vitamin absorption.',
  'Plan your meals in advance to make healthier choices and avoid impulsive eating.',
  "Listen to your body's hunger cues and eat mindfully without distractions.",
]
export function NutritionCalculatorPage() {
  const [step, setStep] = useState<Step>('basic')
  const [calculatingProgress, setCalculatingProgress] = useState(0)
  const [data, setData] = useState<NutritionData>({
    gender: '',
    age: '',
    height: '',
    weight: '',
    goal: '',
    activity: '',
    dietary: [],
    cuisine: '',
    budget: '',
    mealsPerDay: '',
    fastingWindow: '',
    allergies: '',
    targetRate: '',
  })
  const [results, setResults] = useState<NutritionResults | null>(null)
  const [dietPlan, setDietPlan] = useState<DietPlan | null>(null)
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false)
  const [planError, setPlanError] = useState('')
  const [showDietPlan, setShowDietPlan] = useState(false)
  const [currentTipIndex, setCurrentTipIndex] = useState(0)
  const [locationModalOpen, setLocationModalOpen] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string
  } | null>(null)
  const [selectedDay, setSelectedDay] = useState(1)
  const [generatedDays, setGeneratedDays] = useState<number[]>([])
  const [currentGeneratingDay, setCurrentGeneratingDay] = useState<number>(0)
  const [countdownSeconds, setCountdownSeconds] = useState(0)
  const activityFactors = {
    sedentary: 1.2,
    light: 1.375,
    moderate: 1.55,
    active: 1.725,
    very_active: 1.9,
  }
  const activityOptions = [
    {
      id: 'sedentary',
      label: 'Sedentary',
      description: 'Little to no exercise',
      icon: '🛋️',
    },
    {
      id: 'light',
      label: 'Light',
      description: 'Exercise 1-3 days/week',
      icon: '🚶',
    },
    {
      id: 'moderate',
      label: 'Moderate',
      description: 'Exercise 3-5 days/week',
      icon: '🏃',
    },
    {
      id: 'active',
      label: 'Active',
      description: 'Exercise 6-7 days/week',
      icon: '💪',
    },
    {
      id: 'very_active',
      label: 'Very Active',
      description: 'Intense exercise daily',
      icon: '🏋️',
    },
  ]
  const goalOptions = [
    {
      id: 'lose',
      label: 'Lose Weight',
      description: 'Burn fat & get lean',
      icon: '📉',
    },
    {
      id: 'maintain',
      label: 'Maintain Weight',
      description: 'Stay at current weight',
      icon: '⚖️',
    },
    {
      id: 'gain',
      label: 'Gain Weight',
      description: 'Build muscle mass',
      icon: '📈',
    },
  ]
  const dietaryOptions = [
    {
      id: 'halal',
      label: 'Halal',
      icon: '🕌',
    },
    {
      id: 'vegetarian',
      label: 'Vegetarian',
      icon: '🥗',
    },
    {
      id: 'vegan',
      label: 'Vegan',
      icon: '🌱',
    },
    {
      id: 'dairy-free',
      label: 'Dairy Free',
      icon: '🥛',
    },
    {
      id: 'gluten-free',
      label: 'Gluten Free',
      icon: '🌾',
    },
  ]
  const cuisineOptions = [
    {
      id: 'saudi',
      label: 'Saudi/Gulf',
      icon: '🍖',
    },
    {
      id: 'levant',
      label: 'Levantine',
      icon: '🥙',
    },
    {
      id: 'mediterranean',
      label: 'Mediterranean',
      icon: '🍝',
    },
    {
      id: 'international',
      label: 'International',
      icon: '🌍',
    },
  ]
  const budgetOptions = [
    {
      id: 'low',
      label: 'Budget-Friendly',
      icon: '💰',
    },
    {
      id: 'medium',
      label: 'Moderate',
      icon: '💵',
    },
    {
      id: 'high',
      label: 'Premium',
      icon: '💎',
    },
  ]
  const mealColors = {
    Breakfast: 'bg-purple-100 text-purple-900',
    'Morning Snack': 'bg-purple-100 text-purple-900',
    Lunch: 'bg-purple-100 text-purple-900',
    'Evening Snack': 'bg-purple-100 text-purple-900',
    Dinner: 'bg-purple-100 text-purple-900',
    Suhoor: 'bg-purple-100 text-purple-900',
    Iftar: 'bg-purple-100 text-purple-900',
    'Evening Meal': 'bg-purple-100 text-purple-900',
  }
  // Countdown timer for plan generation
  useEffect(() => {
    if (isGeneratingPlan && countdownSeconds > 0) {
      const timer = setTimeout(() => {
        setCountdownSeconds((prev) => prev - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [isGeneratingPlan, countdownSeconds])
  // Rotate nutrition tips while generating plan
  useEffect(() => {
    if (isGeneratingPlan) {
      const interval = setInterval(() => {
        setCurrentTipIndex((prev) => (prev + 1) % nutritionTips.length)
      }, 3000)
      return () => clearInterval(interval)
    }
  }, [isGeneratingPlan])
  // Calculating animation
  useEffect(() => {
    if (step === 'calculating') {
      setCalculatingProgress(0)
      const duration = 2000
      const interval = 20
      const increment = (interval / duration) * 100
      const timer = setInterval(() => {
        setCalculatingProgress((prev) => {
          const next = prev + increment
          if (next >= 100) {
            clearInterval(timer)
            setTimeout(() => {
              const calculatedResults = calculateResults()
              setResults(calculatedResults)
              setStep('results')
            }, 100)
            return 100
          }
          return next
        })
      }, interval)
      return () => clearInterval(timer)
    }
  }, [step])
  const isRamadan = () => {
    const now = new Date()
    const year = now.getFullYear()
    const ramadanStart = new Date(year, 2, 10)
    const ramadanEnd = new Date(year, 3, 9)
    return now >= ramadanStart && now <= ramadanEnd
  }
  const getMealNames = () => {
    if (isRamadan()) {
      return {
        breakfast: 'Suhoor',
        lunch: 'Iftar',
        dinner: 'Evening Meal',
      }
    }
    return {
      breakfast: 'Breakfast',
      lunch: 'Lunch',
      dinner: 'Dinner',
    }
  }
  const calculateResults = (): NutritionResults => {
    const heightM = parseFloat(data.height) / 100
    const weightKg = parseFloat(data.weight)
    const ageYears = parseInt(data.age)
    const bmi = weightKg / (heightM * heightM)
    let bmi_class = ''
    if (bmi < 18.5) bmi_class = 'Underweight'
    else if (bmi < 25) bmi_class = 'Normal'
    else if (bmi < 30) bmi_class = 'Overweight'
    else bmi_class = 'Obese'
    const bmr =
      data.gender === 'male'
        ? 10 * weightKg + 6.25 * parseFloat(data.height) - 5 * ageYears + 5
        : 10 * weightKg + 6.25 * parseFloat(data.height) - 5 * ageYears - 161
    const tdee =
      bmr * activityFactors[data.activity as keyof typeof activityFactors]
    let calorie_target = tdee
    if (data.targetRate === 'mild_loss') calorie_target = tdee - 300
    else if (data.targetRate === 'standard_loss') calorie_target = tdee - 500
    else if (data.targetRate === 'gain') calorie_target = tdee + 300
    let protein_g = 0
    if (data.goal === 'lose') protein_g = weightKg * 2.0
    else if (data.goal === 'maintain') protein_g = weightKg * 1.6
    else if (data.goal === 'gain') protein_g = weightKg * 2.2
    const fat_g = (calorie_target * 0.25) / 9
    const carb_g = (calorie_target - (protein_g * 4 + fat_g * 9)) / 4
    const water_l = Math.max(2.0, Math.round(weightKg * 0.035 * 10) / 10)
    const fiber_g = data.goal === 'lose' ? 35 : 30
    const ideal_weight_range_kg: [number, number] = [
      Math.round(18.5 * heightM * heightM * 10) / 10,
      Math.round(24.9 * heightM * heightM * 10) / 10,
    ]
    return {
      profile: {
        gender: data.gender,
        age: ageYears,
        height_cm: parseFloat(data.height),
        weight_kg: weightKg,
      },
      activity:
        activityOptions.find((a) => a.id === data.activity)?.label ||
        data.activity,
      bmi: Math.round(bmi * 100) / 100,
      bmi_class,
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      calorie_target: Math.round(calorie_target),
      macros: {
        protein_g: Math.round(protein_g),
        fat_g: Math.round(fat_g),
        carb_g: Math.round(carb_g),
      },
      water_l,
      fiber_g,
      ideal_weight_range_kg,
      notes: [
        'Educational purposes only; not medical advice.',
        'Consult healthcare professionals before dietary changes.',
      ],
    }
  }
  const handleNext = () => {
    if (step === 'planning') {
      setStep('calculating')
    } else {
      const steps: Step[] = [
        'basic',
        'goals',
        'activity',
        'dietary',
        'planning',
      ]
      const currentIndex = steps.indexOf(step)
      if (currentIndex < steps.length - 1) {
        setStep(steps[currentIndex + 1])
      }
    }
  }
  const handleBack = () => {
    const steps: Step[] = ['basic', 'goals', 'activity', 'dietary', 'planning']
    const currentIndex = steps.indexOf(step)
    if (currentIndex > 0) {
      setStep(steps[currentIndex - 1])
    }
  }
  const handleReset = () => {
    setStep('basic')
    setData({
      gender: '',
      age: '',
      height: '',
      weight: '',
      goal: '',
      activity: '',
      dietary: [],
      cuisine: '',
      budget: '',
      mealsPerDay: '',
      fastingWindow: '',
      allergies: '',
      targetRate: '',
    })
    setResults(null)
    setShowDietPlan(false)
    setDietPlan(null)
    setSelectedDay(1)
  }
  const canProceed = () => {
    if (step === 'basic')
      return (
        data.gender !== '' &&
        data.age !== '' &&
        data.height !== '' &&
        data.weight !== ''
      )
    if (step === 'goals') return data.goal !== '' && data.targetRate !== ''
    if (step === 'activity') return data.activity !== ''
    if (step === 'dietary')
      return data.dietary.length > 0 && data.cuisine !== ''
    if (step === 'planning')
      return data.budget !== '' && data.mealsPerDay !== ''
    return true
  }
  const getStepNumber = () => {
    const steps: Step[] = ['basic', 'goals', 'activity', 'dietary', 'planning']
    return steps.indexOf(step) + 1
  }
  const generateDietPlan = async () => {
    if (!results) return
    setIsGeneratingPlan(true)
    setPlanError('')
    setShowDietPlan(true)
    setCurrentTipIndex(0)
    setSelectedDay(1)
    setGeneratedDays([])
    setCurrentGeneratingDay(1)
    // Initialize empty diet plan structure
    const emptyPlan: DietPlan = {
      plan_length_days: 7,
      daily: [],
      weekly_shopping_list: [],
      cost_level: budgetOptions.find((b) => b.id === data.budget)?.label || '',
      disclaimer: 'For educational purposes only. Not medical advice.',
    }
    setDietPlan(emptyPlan)
    try {
      const mealNames = getMealNames()
      const allShoppingItems: Array<{
        food: string
        qty_g?: number
        qty_ml?: number
      }> = []
      // Generate each day sequentially
      for (let day = 1; day <= 7; day++) {
        setCurrentGeneratingDay(day)
        const systemPrompt = `You are an expert nutrition planner specializing in Saudi Arabian and Middle Eastern cuisine.
CRITICAL REQUIREMENTS:
1. You MUST generate EXACTLY 1 COMPLETE day (Day ${day})
2. You MUST return ONLY valid, complete JSON (no markdown, no truncation)
3. Include full meal plans with detailed nutritional breakdown
4. The JSON MUST be properly closed with all brackets and braces
TASK: Generate Day ${day} of a 7-day meal plan with COMPLETE nutritional breakdown for each food item.
${isRamadan() ? 'IMPORTANT: This is during Ramadan. Use Suhoor and Iftar meal names.' : 'Use standard meal names: Breakfast, Lunch, Dinner.'}
CONSTRAINTS:
- Hit calorie target: ${results.calorie_target} kcal/day (±100 kcal tolerance)
- Hit macro targets: Protein ${results.macros.protein_g}g, Fat ${results.macros.fat_g}g, Carbs ${results.macros.carb_g}g
- Dietary preferences: ${data.dietary.join(', ') || 'None'}
- Cuisine preference: ${cuisineOptions.find((c) => c.id === data.cuisine)?.label}
- Budget level: ${budgetOptions.find((b) => b.id === data.budget)?.label}
- Meals per day: ${data.mealsPerDay}
- ${data.fastingWindow !== 'none' ? `Fasting window: ${data.fastingWindow}` : 'No fasting'}
- ${data.allergies ? `Allergies: ${data.allergies}` : 'No allergies'}
- All meals must be HALAL
OUTPUT FORMAT:
{
  "day": ${day},
  "total_calories": 2350,
  "total_macros": {"protein_g": 160, "fat_g": 66, "carb_g": 295},
  "meals": [
    {
      "name": "${mealNames.breakfast}",
      "time_range": "7:00 AM - 9:00 AM",
      "items": [
        {"food": "Boiled Eggs", "qty_g": 100, "calories": 155, "protein_g": 13, "fat_g": 11, "carb_g": 1}
      ]
    }
  ],
  "hydration_l": ${results.water_l},
  "shopping_items": [
    {"food": "Eggs", "qty_g": 200}
  ]
}
IMPORTANT: 
- Keep food names simple and concise
- Ensure JSON is complete and valid
- Close all brackets and braces properly
- Include shopping_items list for this day`
        const requestBody = {
          model: 'gpt-4-turbo',
          messages: [
            {
              role: 'system',
              content: systemPrompt,
            },
            {
              role: 'user',
              content: `Generate Day ${day} meal plan. Return ONLY valid, complete JSON. Ensure the JSON is properly closed.`,
            },
          ],
          temperature: 0.7,
          max_tokens: 4000,
        }
        const response = await fetch(
          'https://requests.magicpatterns.com/openai/proxy',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
          },
        )
        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(
            `API request failed for Day ${day} (${response.status}): ${errorText}`,
          )
        }
        const aiResponse = await response.json()
        const content = aiResponse.choices?.[0]?.message?.content
        if (!content) {
          throw new Error(`No content received for Day ${day}`)
        }
        // Parse the day's data
        let dayPlan: DailyPlan & {
          shopping_items?: Array<{
            food: string
            qty_g?: number
            qty_ml?: number
          }>
        }
        try {
          dayPlan = JSON.parse(content)
        } catch (e1) {
          // Try extracting from markdown
          const codeBlockMatch = content.match(
            /```(?:json)?\s*([\s\S]*?)\s*```/,
          )
          if (codeBlockMatch) {
            try {
              dayPlan = JSON.parse(codeBlockMatch[1])
            } catch (e2) {
              // Try cleanup
              const cleaned = content
                .replace(/```(?:json)?/g, '')
                .replace(/```/g, '')
                .trim()
              try {
                dayPlan = JSON.parse(cleaned)
              } catch (e3) {
                throw new Error(
                  `Failed to parse Day ${day}. Error: ${e3 instanceof Error ? e3.message : String(e3)}`,
                )
              }
            }
          } else {
            throw new Error(
              `Invalid JSON for Day ${day}. Error: ${e1 instanceof Error ? e1.message : String(e1)}`,
            )
          }
        }
        // Collect shopping items
        if (dayPlan.shopping_items) {
          allShoppingItems.push(...dayPlan.shopping_items)
        }
        // Update the diet plan with this day
        setDietPlan((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            daily: [...prev.daily, dayPlan],
          }
        })
        // Mark this day as generated
        setGeneratedDays((prev) => [...prev, day])
        // Auto-switch to the next day if not the last day
        if (day < 7) {
          setSelectedDay(day + 1)
        }
      }
      // After all days are generated, consolidate shopping list
      const consolidatedShopping = consolidateShoppingList(allShoppingItems)
      setDietPlan((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          weekly_shopping_list: consolidatedShopping,
        }
      })
    } catch (error) {
      console.error('Diet plan generation error:', error)
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred'
      setPlanError(errorMessage)
    } finally {
      setIsGeneratingPlan(false)
      setCurrentGeneratingDay(0)
    }
  }
  // Helper function to consolidate shopping items
  const consolidateShoppingList = (
    items: Array<{
      food: string
      qty_g?: number
      qty_ml?: number
    }>,
  ) => {
    const consolidated: {
      [key: string]: {
        qty_g?: number
        qty_ml?: number
      }
    } = {}
    items.forEach((item) => {
      const key = item.food.toLowerCase()
      if (!consolidated[key]) {
        consolidated[key] = {
          qty_g: 0,
          qty_ml: 0,
        }
      }
      if (item.qty_g) consolidated[key].qty_g! += item.qty_g
      if (item.qty_ml) consolidated[key].qty_ml! += item.qty_ml
    })
    return Object.entries(consolidated).map(([food, quantities]) => ({
      food: food.charAt(0).toUpperCase() + food.slice(1),
      qty_g: quantities.qty_g || undefined,
      qty_ml: quantities.qty_ml || undefined,
    }))
  }
  const downloadDietPlanPDF = () => {
    alert(
      'Diet Plan PDF download will be implemented with a PDF generation library',
    )
  }
  const downloadIngredientsListPDF = () => {
    alert(
      'Ingredients List PDF download will be implemented with a PDF generation library',
    )
  }
  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <AnnouncementBar />
      <MobileDeliverToPill
        selectedLocation={selectedLocation}
        onOpenLocationModal={() => setLocationModalOpen(true)}
      />
      <Header
        locationModalOpen={locationModalOpen}
        setLocationModalOpen={setLocationModalOpen}
        selectedLocation={selectedLocation}
        setSelectedLocation={setSelectedLocation}
      />
      <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]">
        {/* Mobile Header Overlay */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-50">
          <MobileHomeHeader
            selectedLocation={selectedLocation}
            onOpenLocationModal={() => setLocationModalOpen(true)}
            alwaysWhiteBackground={true}
          />
        </div>
        <PageHeroBreadcrumb
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Nutrition Calculator' },
          ]}
          title="AI Nutrition & Diet Plan Creator"
          description="Calculate your daily nutritional needs and get personalized recommendations for a balanced diet tailored to your goals."
        />
        <div className="pb-8">
          <div className="container mx-auto px-4 max-w-4xl">
            {/* Header */}
            <div className="text-center mb-8 mt-8">
              <div className="inline-flex items-center justify-center mb-4">
                <SparklesIcon className="w-8 h-8 text-purple-600 mr-2" />
                <BrainIcon className="w-8 h-8 text-[#1E2B55]" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1E2B55] mb-2">
                Get Your Personalized Plan
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Answer a few questions to receive AI-generated meal plans
                tailored to your goals
              </p>
            </div>
            {/* Progress Bar */}
            {step !== 'results' && step !== 'calculating' && (
              <div className="mb-8">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Step {getStepNumber()} of 5
                  </span>
                  <span className="text-sm font-medium text-gray-700">
                    {Math.round((getStepNumber() / 5) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-purple-600 to-purple-700 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${(getStepNumber() / 5) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>
            )}
            {/* Step Content */}
            <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
              {/* Step 1: Basic Info */}
              {step === 'basic' && (
                <div>
                  <div className="flex items-center mb-6">
                    <UserIcon className="w-6 h-6 text-[#1E2B55] mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">
                      Basic Information
                    </h2>
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Gender *
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        className={`p-4 rounded-xl border-2 transition-all ${data.gender === 'male' ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-[#1E2B55]'}`}
                        onClick={() =>
                          setData({
                            ...data,
                            gender: 'male',
                          })
                        }
                      >
                        <div className="text-3xl mb-2">👨</div>
                        <div className="font-bold">Male</div>
                      </button>
                      <button
                        className={`p-4 rounded-xl border-2 transition-all ${data.gender === 'female' ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-[#1E2B55]'}`}
                        onClick={() =>
                          setData({
                            ...data,
                            gender: 'female',
                          })
                        }
                      >
                        <div className="text-3xl mb-2">👩</div>
                        <div className="font-bold">Female</div>
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <NumericInput
                      label="Age"
                      value={data.age}
                      onChange={(value) =>
                        setData({
                          ...data,
                          age: value,
                        })
                      }
                      placeholder="25"
                      min={14}
                      max={80}
                      required
                    />
                    <NumericInput
                      label="Height (cm)"
                      value={data.height}
                      onChange={(value) =>
                        setData({
                          ...data,
                          height: value,
                        })
                      }
                      placeholder="170"
                      min={100}
                      max={250}
                      required
                    />
                    <NumericInput
                      label="Weight (kg)"
                      value={data.weight}
                      onChange={(value) =>
                        setData({
                          ...data,
                          weight: value,
                        })
                      }
                      placeholder="70"
                      min={30}
                      max={300}
                      step={0.5}
                      required
                    />
                  </div>
                </div>
              )}
              {/* Step 2: Goals */}
              {step === 'goals' && (
                <div>
                  <div className="flex items-center mb-6">
                    <TargetIcon className="w-6 h-6 text-[#1E2B55] mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">
                      Your Goals
                    </h2>
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Primary Goal *
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {goalOptions.map((goal) => (
                        <button
                          key={goal.id}
                          className={`p-4 rounded-xl border-2 transition-all ${data.goal === goal.id ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-[#1E2B55]'}`}
                          onClick={() =>
                            setData({
                              ...data,
                              goal: goal.id as any,
                            })
                          }
                        >
                          <div className="text-3xl mb-2">{goal.icon}</div>
                          <div className="font-bold mb-1">{goal.label}</div>
                          <div className="text-sm text-gray-600">
                            {goal.description}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Target Rate *
                    </label>
                    <div className="space-y-3">
                      <button
                        className={`w-full p-4 rounded-xl border-2 text-left transition-all ${data.targetRate === 'mild_loss' ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-[#1E2B55]'}`}
                        onClick={() =>
                          setData({
                            ...data,
                            targetRate: 'mild_loss',
                          })
                        }
                      >
                        <div className="font-bold mb-1">
                          Mild Deficit (-300 cal/day)
                        </div>
                        <div className="text-sm text-gray-600">
                          Slow and steady weight loss
                        </div>
                      </button>
                      <button
                        className={`w-full p-4 rounded-xl border-2 text-left transition-all ${data.targetRate === 'standard_loss' ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-[#1E2B55]'}`}
                        onClick={() =>
                          setData({
                            ...data,
                            targetRate: 'standard_loss',
                          })
                        }
                      >
                        <div className="font-bold mb-1">
                          Standard Deficit (-500 cal/day)
                        </div>
                        <div className="text-sm text-gray-600">
                          Balanced weight loss approach
                        </div>
                      </button>
                      <button
                        className={`w-full p-4 rounded-xl border-2 text-left transition-all ${data.targetRate === 'gain' ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-[#1E2B55]'}`}
                        onClick={() =>
                          setData({
                            ...data,
                            targetRate: 'gain',
                          })
                        }
                      >
                        <div className="font-bold mb-1">
                          Surplus (+300 cal/day)
                        </div>
                        <div className="text-sm text-gray-600">
                          Lean muscle gain
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {/* Step 3: Activity Level */}
              {step === 'activity' && (
                <div>
                  <div className="flex items-center mb-6">
                    <ActivityIcon className="w-6 h-6 text-[#1E2B55] mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">
                      Activity Level
                    </h2>
                  </div>
                  <div className="space-y-3">
                    {activityOptions.map((option) => (
                      <button
                        key={option.id}
                        className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center ${data.activity === option.id ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-[#1E2B55]'}`}
                        onClick={() =>
                          setData({
                            ...data,
                            activity: option.id as any,
                          })
                        }
                      >
                        <div className="text-3xl mr-4">{option.icon}</div>
                        <div className="flex-1">
                          <div className="font-bold text-lg">
                            {option.label}
                          </div>
                          <div className="text-sm text-gray-600">
                            {option.description}
                          </div>
                        </div>
                        {data.activity === option.id && (
                          <CheckCircleIcon className="w-6 h-6 text-[#1E2B55]" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* Step 4: Dietary Preferences */}
              {step === 'dietary' && (
                <div>
                  <div className="flex items-center mb-6">
                    <UtensilsIcon className="w-6 h-6 text-[#1E2B55] mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">
                      Dietary Preferences
                    </h2>
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Select all that apply *
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {dietaryOptions.map((option) => (
                        <button
                          key={option.id}
                          className={`p-3 rounded-xl border-2 transition-all ${data.dietary.includes(option.id) ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-[#1E2B55]'}`}
                          onClick={() => {
                            const updated = data.dietary.includes(option.id)
                              ? data.dietary.filter((d) => d !== option.id)
                              : [...data.dietary, option.id]
                            setData({
                              ...data,
                              dietary: updated,
                            })
                          }}
                        >
                          <div className="text-2xl mb-1">{option.icon}</div>
                          <div className="font-medium text-sm">
                            {option.label}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Preferred Cuisine *
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {cuisineOptions.map((option) => (
                        <button
                          key={option.id}
                          className={`p-3 rounded-xl border-2 transition-all ${data.cuisine === option.id ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-[#1E2B55]'}`}
                          onClick={() =>
                            setData({
                              ...data,
                              cuisine: option.id,
                            })
                          }
                        >
                          <div className="text-2xl mb-1">{option.icon}</div>
                          <div className="font-medium text-sm">
                            {option.label}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Allergies or Intolerances (optional)
                    </label>
                    <input
                      type="text"
                      value={data.allergies}
                      onChange={(e) =>
                        setData({
                          ...data,
                          allergies: e.target.value,
                        })
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55]"
                      placeholder="e.g., peanuts, shellfish, lactose"
                    />
                  </div>
                </div>
              )}
              {/* Step 5: Meal Planning */}
              {step === 'planning' && (
                <div>
                  <div className="flex items-center mb-6">
                    <ClockIcon className="w-6 h-6 text-[#1E2B55] mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">
                      Meal Planning
                    </h2>
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Budget Level *
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {budgetOptions.map((option) => (
                        <button
                          key={option.id}
                          className={`p-4 rounded-xl border-2 transition-all ${data.budget === option.id ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-[#1E2B55]'}`}
                          onClick={() =>
                            setData({
                              ...data,
                              budget: option.id as any,
                            })
                          }
                        >
                          <div className="text-3xl mb-2">{option.icon}</div>
                          <div className="font-bold">{option.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Meals Per Day *
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {['3', '4', '5', '6'].map((num) => (
                        <button
                          key={num}
                          className={`p-4 rounded-xl border-2 transition-all ${data.mealsPerDay === num ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-[#1E2B55]'}`}
                          onClick={() =>
                            setData({
                              ...data,
                              mealsPerDay: num as any,
                            })
                          }
                        >
                          <div className="font-bold text-2xl">{num}</div>
                          <div className="text-sm text-gray-600">meals</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Fasting Window (optional)
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {[
                        {
                          id: 'none',
                          label: 'No Fasting',
                        },
                        {
                          id: '16:8',
                          label: '16:8 (16h fast)',
                        },
                        {
                          id: '14:10',
                          label: '14:10 (14h fast)',
                        },
                      ].map((option) => (
                        <button
                          key={option.id}
                          className={`p-3 rounded-xl border-2 transition-all ${data.fastingWindow === option.id ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-[#1E2B55]'}`}
                          onClick={() =>
                            setData({
                              ...data,
                              fastingWindow: option.id as any,
                            })
                          }
                        >
                          <div className="font-medium">{option.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {/* Calculating State */}
              {step === 'calculating' && (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center mb-6">
                    <div className="w-16 h-16 border-4 border-[#1E2B55] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Calculating Your Personalized Results
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Analyzing your profile and generating nutrition targets...
                  </p>
                  <div className="max-w-md mx-auto">
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-[#1E2B55] to-purple-600 h-3 rounded-full transition-all duration-300"
                        style={{
                          width: `${calculatingProgress}%`,
                        }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      {Math.round(calculatingProgress)}%
                    </p>
                  </div>
                </div>
              )}
              {/* Step 6: Results */}
              {step === 'results' && results && (
                <div>
                  <div className="text-center mb-8">
                    <div className="inline-block p-3 bg-green-100 rounded-full mb-3">
                      <CheckCircleIcon className="w-8 h-8 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold mb-2">
                      Your Nutrition Plan is Ready!
                    </h2>
                    <p className="text-gray-600">
                      Here are your personalized nutrition targets
                    </p>
                  </div>
                  {/* Key Metrics Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200">
                      <div className="flex items-center mb-3">
                        <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                          <ScaleIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900">BMI</h3>
                          <p className="text-xs text-gray-600">
                            {results.bmi_class}
                          </p>
                        </div>
                      </div>
                      <p className="text-4xl font-bold text-blue-600">
                        {results.bmi}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border-2 border-orange-200">
                      <div className="flex items-center mb-3">
                        <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mr-3">
                          <FlameIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900">BMR</h3>
                          <p className="text-xs text-gray-600">Base calories</p>
                        </div>
                      </div>
                      <p className="text-4xl font-bold text-orange-600">
                        {results.bmr}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border-2 border-green-200">
                      <div className="flex items-center mb-3">
                        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mr-3">
                          <TrendingUpIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900">TDEE</h3>
                          <p className="text-xs text-gray-600">
                            Maintenance calories
                          </p>
                        </div>
                      </div>
                      <p className="text-4xl font-bold text-green-600">
                        {results.tdee}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border-2 border-purple-200">
                      <div className="flex items-center mb-3">
                        <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mr-3">
                          <TargetIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900">
                            Target Calories
                          </h3>
                          <p className="text-xs text-gray-600">Daily goal</p>
                        </div>
                      </div>
                      <p className="text-4xl font-bold text-purple-600">
                        {results.calorie_target}
                      </p>
                    </div>
                  </div>
                  {/* Macros */}
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 mb-8 border border-gray-200">
                    <h3 className="font-bold text-xl mb-4 flex items-center">
                      <HeartIcon className="w-6 h-6 text-red-500 mr-2" />
                      Daily Macronutrient Targets
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white rounded-xl p-5 shadow-sm">
                        <div className="text-sm font-semibold text-gray-700 mb-2">
                          Protein
                        </div>
                        <div className="text-3xl font-bold text-[#1E2B55] mb-1">
                          {results.macros.protein_g}g
                        </div>
                        <div className="text-xs text-gray-500">
                          {Math.round(
                            ((results.macros.protein_g * 4) /
                              results.calorie_target) *
                              100,
                          )}
                          % of calories
                        </div>
                      </div>
                      <div className="bg-white rounded-xl p-5 shadow-sm">
                        <div className="text-sm font-semibold text-gray-700 mb-2">
                          Carbs
                        </div>
                        <div className="text-3xl font-bold text-[#1E2B55] mb-1">
                          {results.macros.carb_g}g
                        </div>
                        <div className="text-xs text-gray-500">
                          {Math.round(
                            ((results.macros.carb_g * 4) /
                              results.calorie_target) *
                              100,
                          )}
                          % of calories
                        </div>
                      </div>
                      <div className="bg-white rounded-xl p-5 shadow-sm">
                        <div className="text-sm font-semibold text-gray-700 mb-2">
                          Fat
                        </div>
                        <div className="text-3xl font-bold text-[#1E2B55] mb-1">
                          {results.macros.fat_g}g
                        </div>
                        <div className="text-xs text-gray-500">
                          {Math.round(
                            ((results.macros.fat_g * 9) /
                              results.calorie_target) *
                              100,
                          )}
                          % of calories
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Other Targets */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
                      <div className="flex items-center mb-3">
                        <DropletIcon className="w-6 h-6 text-blue-600 mr-2" />
                        <h3 className="font-bold">Water Intake</h3>
                      </div>
                      <p className="text-3xl font-bold text-blue-600 mb-1">
                        {results.water_l}L
                      </p>
                      <p className="text-sm text-gray-600">per day</p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-6 border-2 border-green-200">
                      <div className="flex items-center mb-3">
                        <UtensilsIcon className="w-6 h-6 text-green-600 mr-2" />
                        <h3 className="font-bold">Fiber</h3>
                      </div>
                      <p className="text-3xl font-bold text-green-600 mb-1">
                        {results.fiber_g}g
                      </p>
                      <p className="text-sm text-gray-600">per day</p>
                    </div>
                  </div>
                  {/* AI Diet Plan Section */}
                  {!showDietPlan ? (
                    <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl p-8 text-white text-center mb-8">
                      <SparklesIcon className="w-12 h-12 mx-auto mb-4" />
                      <h3 className="text-2xl font-bold mb-2">
                        Generate Your AI Diet Plan
                      </h3>
                      <p className="mb-6 text-purple-100">
                        Get a personalized 7-day meal plan based on your
                        preferences and nutrition targets
                      </p>
                      <button
                        onClick={generateDietPlan}
                        className="bg-white text-purple-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors inline-flex items-center"
                      >
                        <SparklesIcon className="w-5 h-5 mr-2" />
                        Generate 7-Day Plan
                      </button>
                    </div>
                  ) : (
                    <div className="mb-8">
                      {/* Error State */}
                      {planError && !isGeneratingPlan && (
                        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-4">
                          <div className="flex items-start">
                            <AlertCircleIcon className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-1" />
                            <div className="flex-1">
                              <h3 className="font-bold text-red-900 mb-2">
                                Generation Failed
                              </h3>
                              <div className="bg-white rounded-lg p-4 mb-3 border border-red-200">
                                <pre className="text-xs text-red-700 whitespace-pre-wrap break-words font-mono max-h-96 overflow-y-auto">
                                  {planError}
                                </pre>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={generateDietPlan}
                                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                                >
                                  Try Again
                                </button>
                                <button
                                  onClick={() => {
                                    setShowDietPlan(false)
                                    setPlanError('')
                                    setGeneratedDays([])
                                    setCurrentGeneratingDay(0)
                                  }}
                                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Generated Plan Display */}
                      {dietPlan && (
                        <div className="bg-white rounded-xl border-2 border-purple-200">
                          {/* Header */}
                          <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 rounded-t-xl">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mr-3">
                                  <ChefHatIcon className="w-6 h-6 text-purple-600" />
                                </div>
                                <div>
                                  <h3 className="text-xl font-bold">
                                    Your 7-Day Diet Plan
                                  </h3>
                                  <p className="text-sm text-purple-100">
                                    {isGeneratingPlan
                                      ? `Generating Day ${currentGeneratingDay} of 7...`
                                      : 'AI-generated & culturally tailored'}
                                  </p>
                                </div>
                              </div>
                              {!isGeneratingPlan &&
                                generatedDays.length === 7 && (
                                  <div className="flex gap-2">
                                    <button
                                      onClick={downloadDietPlanPDF}
                                      className="px-4 py-2 bg-white text-purple-600 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2 text-sm font-medium"
                                    >
                                      <DownloadIcon className="w-4 h-4" />
                                      Diet Plan
                                    </button>
                                    <button
                                      onClick={downloadIngredientsListPDF}
                                      className="px-4 py-2 bg-white text-purple-600 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2 text-sm font-medium"
                                    >
                                      <DownloadIcon className="w-4 h-4" />
                                      Ingredients
                                    </button>
                                  </div>
                                )}
                            </div>
                          </div>
                          {/* Day Tabs */}
                          <div className="border-b border-gray-200 overflow-x-auto">
                            <div className="flex min-w-max">
                              {[1, 2, 3, 4, 5, 6, 7].map((dayNum) => {
                                const isGenerated =
                                  generatedDays.includes(dayNum)
                                const isGenerating =
                                  currentGeneratingDay === dayNum
                                const isActive = selectedDay === dayNum
                                return (
                                  <button
                                    key={dayNum}
                                    onClick={() =>
                                      isGenerated && setSelectedDay(dayNum)
                                    }
                                    disabled={!isGenerated}
                                    className={`px-6 py-3 font-medium text-sm transition-colors relative ${
                                      isActive
                                        ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-600'
                                        : isGenerated
                                          ? 'text-gray-600 hover:bg-gray-50'
                                          : 'text-gray-400 cursor-not-allowed'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <span>Day {dayNum}</span>
                                      {isGenerating && (
                                        <Loader2Icon className="w-4 h-4 animate-spin text-purple-600" />
                                      )}
                                      {isGenerated && !isGenerating && (
                                        <CheckCircleIcon className="w-4 h-4 text-green-600" />
                                      )}
                                    </div>
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                          {/* Preparing Message */}
                          {isGeneratingPlan && (
                            <div className="p-6 bg-purple-50 border-b border-purple-200">
                              <div className="flex items-center justify-center gap-3">
                                <Loader2Icon className="w-5 h-5 text-purple-600 animate-spin" />
                                <span className="text-purple-700 font-medium">
                                  Preparing remaining days...
                                </span>
                              </div>
                            </div>
                          )}
                          {/* Selected Day Content */}
                          {dietPlan.daily
                            .filter((day) => day.day === selectedDay)
                            .map((day) => (
                              <div key={day.day} className="p-6">
                                {/* Day Summary */}
                                <div className="mb-6 pb-4 border-b border-gray-200">
                                  <div className="flex items-center justify-between mb-4">
                                    <h4 className="text-lg font-bold text-gray-900">
                                      Day {day.day} Overview
                                    </h4>
                                    <div className="flex items-center gap-4">
                                      <span className="text-sm font-medium text-gray-600">
                                        <FlameIcon className="w-4 h-4 inline mr-1 text-orange-500" />
                                        {day.total_calories} kcal
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                {/* Meals Table */}
                                <div className="overflow-x-auto">
                                  <table className="w-full border-collapse">
                                    <thead>
                                      <tr className="bg-purple-600 text-white">
                                        <th className="text-left p-3 font-bold border border-purple-700">
                                          Meals
                                        </th>
                                        <th className="text-left p-3 font-bold border border-purple-700">
                                          Food Items
                                        </th>
                                        <th className="text-center p-3 font-bold border border-purple-700">
                                          Amount (g)
                                        </th>
                                        <th className="text-center p-3 font-bold border border-purple-700">
                                          Calories (g)
                                        </th>
                                        <th className="text-center p-3 font-bold border border-purple-700">
                                          Protein (g)
                                        </th>
                                        <th className="text-center p-3 font-bold border border-purple-700">
                                          Fat (g)
                                        </th>
                                        <th className="text-center p-3 font-bold border border-purple-700">
                                          Carb (g)
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {day.meals.map((meal, mealIdx) => (
                                        <Fragment key={mealIdx}>
                                          {meal.items.map((item, itemIdx) => (
                                            <tr
                                              key={itemIdx}
                                              className="border border-gray-300 hover:bg-gray-50"
                                            >
                                              {itemIdx === 0 && (
                                                <td
                                                  rowSpan={meal.items.length}
                                                  className={`p-4 font-bold align-top border border-gray-300 ${mealColors[meal.name as keyof typeof mealColors]}`}
                                                >
                                                  <div className="font-bold text-sm mb-1">
                                                    {meal.name}
                                                  </div>
                                                  <div className="text-xs font-normal">
                                                    {meal.time_range}
                                                  </div>
                                                </td>
                                              )}
                                              <td className="p-3 text-sm text-gray-700 border border-gray-300">
                                                {item.food}
                                              </td>
                                              <td className="p-3 text-sm text-center text-gray-700 border border-gray-300">
                                                {item.qty_g ||
                                                  item.qty_ml ||
                                                  '-'}
                                              </td>
                                              <td className="p-3 text-sm text-center text-gray-700 border border-gray-300">
                                                {item.calories}
                                              </td>
                                              <td className="p-3 text-sm text-center text-gray-700 border border-gray-300">
                                                {item.protein_g}
                                              </td>
                                              <td className="p-3 text-sm text-center text-gray-700 border border-gray-300">
                                                {item.fat_g}
                                              </td>
                                              <td className="p-3 text-sm text-center text-gray-700 border border-gray-300">
                                                {item.carb_g}
                                              </td>
                                            </tr>
                                          ))}
                                        </Fragment>
                                      ))}
                                      {/* Total Row */}
                                      <tr className="bg-gradient-to-r from-yellow-100 to-yellow-50 font-bold border-t-2 border-gray-400">
                                        <td
                                          colSpan={3}
                                          className="p-4 text-right border border-gray-300"
                                        >
                                          Total
                                        </td>
                                        <td className="p-4 text-center bg-yellow-200 border border-gray-300">
                                          {day.total_calories}
                                        </td>
                                        <td className="p-4 text-center bg-green-200 border border-gray-300">
                                          {day.total_macros.protein_g}
                                        </td>
                                        <td className="p-4 text-center bg-red-200 border border-gray-300">
                                          {day.total_macros.fat_g}
                                        </td>
                                        <td className="p-4 text-center bg-blue-200 border border-gray-300">
                                          {day.total_macros.carb_g}
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                                {/* Hydration */}
                                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                  <div className="flex items-center">
                                    <DropletIcon className="w-5 h-5 text-blue-600 mr-2" />
                                    <span className="font-bold text-gray-900 mr-2">
                                      Daily Water Intake:
                                    </span>
                                    <span className="text-lg font-bold text-blue-600">
                                      {day.hydration_l}L
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          {/* Weekly Shopping List - Only show when all days are generated */}
                          {!isGeneratingPlan &&
                            generatedDays.length === 7 &&
                            dietPlan.weekly_shopping_list &&
                            dietPlan.weekly_shopping_list.length > 0 && (
                              <div className="border-t border-gray-200 p-6 bg-gray-50">
                                <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                                  <ShoppingCartIcon className="w-5 h-5 text-green-600 mr-2" />
                                  Weekly Ingredients Summary
                                </h4>
                                <div className="overflow-x-auto">
                                  <table className="w-full border-collapse">
                                    <thead>
                                      <tr className="bg-green-600 text-white">
                                        <th className="text-left p-3 font-bold border border-green-700">
                                          Ingredient
                                        </th>
                                        <th className="text-center p-3 font-bold border border-green-700">
                                          Total Quantity
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {dietPlan.weekly_shopping_list.map(
                                        (item, idx) => (
                                          <tr
                                            key={idx}
                                            className="border border-gray-300 hover:bg-white"
                                          >
                                            <td className="p-3 text-sm text-gray-700 border border-gray-300">
                                              {item.food}
                                            </td>
                                            <td className="p-3 text-sm text-center text-gray-700 border border-gray-300">
                                              {item.qty_g && `${item.qty_g}g`}
                                              {item.qty_ml &&
                                                `${item.qty_ml}ml`}
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                                <div className="mt-4 pt-4 border-t border-gray-300">
                                  <p className="text-sm text-gray-600 flex items-center">
                                    <DollarSignIcon className="w-4 h-4 inline mr-1 text-green-600" />
                                    Estimated cost level:{' '}
                                    <span className="font-bold ml-1">
                                      {dietPlan.cost_level}
                                    </span>
                                  </p>
                                </div>
                              </div>
                            )}
                          {/* Disclaimer */}
                          <div className="border-t border-gray-200 p-6">
                            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                              <div className="flex items-start">
                                <AlertCircleIcon className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0" />
                                <p className="text-sm text-gray-700">
                                  {dietPlan.disclaimer}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Disclaimer */}
                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6">
                    <div className="flex items-start">
                      <AlertCircleIcon className="w-6 h-6 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <h3 className="font-bold text-gray-900 mb-2">
                          Important Disclaimer
                        </h3>
                        <ul className="text-sm text-gray-700 space-y-1">
                          {results.notes.map((note, index) => (
                            <li key={index}>• {note}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                {step !== 'basic' &&
                  step !== 'calculating' &&
                  step !== 'results' && (
                    <button
                      onClick={handleBack}
                      className="flex items-center px-6 py-3 border-2 border-[#1E2B55] text-[#1E2B55] font-semibold rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <ArrowLeftIcon className="w-5 h-5 mr-2" />
                      Back
                    </button>
                  )}
                {step !== 'results' && step !== 'calculating' && (
                  <button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className={`ml-auto flex items-center px-6 py-3 font-semibold rounded-lg transition-colors ${canProceed() ? 'bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] text-white hover:shadow-lg' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                  >
                    {step === 'planning' ? 'Calculate Results' : 'Continue'}
                    <ArrowRightIcon className="w-5 h-5 ml-2" />
                  </button>
                )}
                {step === 'results' && (
                  <button
                    onClick={handleReset}
                    className="ml-auto flex items-center px-6 py-3 bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] text-white font-semibold rounded-lg hover:shadow-lg transition-colors"
                  >
                    <SparklesIcon className="w-5 h-5 mr-2" />
                    Start New Plan
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <MobileFooterNav />
      <PharmacistChat />
    </div>
  )
}
const NumericInput = ({
  label,
  value,
  onChange,
  placeholder,
  min,
  max,
  step = 1,
  required = false,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder: string
  min?: number
  max?: number
  step?: number
  required?: boolean
}) => {
  const increment = () => {
    const current = parseFloat(value) || 0
    const newValue = Math.min(max || Infinity, current + step)
    onChange(newValue.toString())
  }
  const decrement = () => {
    const current = parseFloat(value) || 0
    const newValue = Math.max(min || 0, current - step)
    onChange(newValue.toString())
  }
  // Prevent buttons from stealing focus and stop event propagation
  const handleButtonClick = (action: () => void) => (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    action()
  }
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleButtonClick(decrement)}
          onMouseDown={(e) => e.preventDefault()}
          className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex-shrink-0"
          tabIndex={-1}
        >
          <MinusIcon className="w-5 h-5 text-gray-700" />
        </button>
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] text-center font-semibold"
          placeholder={placeholder}
          inputMode="decimal"
        />
        <button
          type="button"
          onClick={handleButtonClick(increment)}
          onMouseDown={(e) => e.preventDefault()}
          className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex-shrink-0"
          tabIndex={-1}
        >
          <PlusIcon className="w-5 h-5 text-gray-700" />
        </button>
      </div>
    </div>
  )
}
